/*
  tile.co.uk — Main Header JS
  File: assets/section-main-header.js
*/

(function () {
  'use strict';

  class TcSiteHeader extends HTMLElement {

    connectedCallback() {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._init();
        });
      });
    }

    _init() {
      this._bindTierOne();
      this._bindMobileToggle();
      this._bindScrollState();
      this._bindClickOutside();
      this._bindEscape();
      this._bindFooterHeightSync(); // ← new
    }

    // ── Footer height sync ──────────────────────────────────────────────────
    // Keeps --tc-drawer-footer-height matched to the footer's real rendered
    // height, so mobile submenus (bottom: var(--tc-drawer-footer-height))
    // always end exactly where the footer begins, regardless of content
    // changes (signed-in vs signed-out, text wrapping, safe-area insets, etc).

    _syncFooterHeight() {
      const footer = this.querySelector('.tc-mobile-drawer__footer');
      if (!footer) return;
      const height = footer.getBoundingClientRect().height;
      if (height > 0) {
        document.documentElement.style.setProperty('--tc-drawer-footer-height', `${height}px`);
      }
    }

    _bindFooterHeightSync() {
      window.addEventListener('resize', () => this._syncFooterHeight());
      window.addEventListener('orientationchange', () => this._syncFooterHeight());
    }

    // ── Tier one nav items ──────────────────────────────────────────────────

    _bindTierOne() {
      const nav = this.querySelector('.tc-tier-one-nav');
      if (!nav) {
        console.warn('[TC Nav] Could not find .tc-tier-one-nav');
        return;
      }

      // Tier one — click via event delegation
      nav.addEventListener('click', (e) => {
        const btn = e.target.closest('.tc-tier-one-item');
        if (!btn) return;

        // If it's a plain link (no dropdown), let it navigate
        if (btn.tagName === 'A') return;

        e.preventDefault();
        e.stopPropagation();

        const isActive = btn.classList.contains('js-is-active');
        this._closeAllDropdowns();

        if (!isActive) {
          btn.classList.add('js-is-active');
          btn.setAttribute('aria-expanded', 'true');
          document.body.classList.add('tc-megamenu-open');

          // Activate first tier-two item in the newly opened dropdown
          const dropdown = btn.nextElementSibling;
          if (dropdown) {
            const firstTierTwo = dropdown.querySelector('.tc-tier-two-item');
            if (firstTierTwo) this._activateTierTwoItem(firstTierTwo);
          }
        }
      });

      // Tier two — click via event delegation
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('.tc-tier-two-item');
        if (!btn) return;
            // If it's a direct link with no tier-three children, let it navigate
        if (btn.classList.contains('tc-tier-two-item--link')) return;
        e.preventDefault();
        e.stopPropagation();
        this._activateTierTwoItem(btn);
      });

      // Tier two — hover via event delegation (desktop only)
      this.addEventListener('mouseover', (e) => {
        if (window.innerWidth < 1100) return;
        const btn = e.target.closest('.tc-tier-two-item');
        if (!btn) return;
        if (btn.classList.contains('tc-tier-two-item--link')) return; // ← add this
        this._activateTierTwoItem(btn);
      });
    }

    _activateTierTwoItem(btn) {
      const targetId = btn.getAttribute('data-target');
      if (!targetId) return;

      const flyout = btn.closest('.tc-flyout');
      if (!flyout) return;

      flyout.querySelectorAll('.tc-tier-two-item').forEach(b => {
        b.classList.remove('tc-tier-two-item--active');
      });

      flyout.querySelectorAll('[data-tier-three-panel]').forEach(panel => {
        panel.classList.remove('tc-tier-three-panel--active');
      });

      btn.classList.add('tc-tier-two-item--active');

      const panel = document.getElementById(targetId);
      if (panel) panel.classList.add('tc-tier-three-panel--active');
    }

    _closeAllDropdowns() {
      this.querySelectorAll('.tc-tier-one-item.js-is-active').forEach(btn => {
        btn.classList.remove('js-is-active');
        btn.setAttribute('aria-expanded', 'false');
      });
      document.body.classList.remove('tc-megamenu-open');
    }

    // ── Mobile drawer ───────────────────────────────────────────────────────

    _bindMobileToggle() {
      // Hamburger open button lives outside the drawer — bind on custom element
      this.addEventListener('click', (e) => {
        if (
          e.target.closest('[data-site-header-el="navigationToggleBtn"]') ||
          e.target.closest('.tc-action-btn--nav-toggle')
        ) {
          const drawer = this.querySelector('[data-mobile-drawer]');
          if (!drawer) return;
          const isOpen = drawer.getAttribute('aria-hidden') === 'false';
          isOpen ? this._closeMobileDrawer() : this._openMobileDrawer();
        }
      });

      // All drawer-internal clicks scoped to the drawer only.
      // This prevents desktop nav buttons sharing class names from firing,
      // and prevents event bubbling from activating nested submenus.
      const drawer = this.querySelector('[data-mobile-drawer]');
      if (!drawer) return;

      drawer.addEventListener('click', (e) => {
        e.stopPropagation();

        // Tap backdrop
        if (e.target === drawer) {
          this._closeMobileDrawer();
          return;
        }

        // Close button inside drawer header
        if (e.target.closest('.tc-mobile-close')) {
          this._closeMobileDrawer();
          return;
        }

        // Tier-one item — only if NOT inside an already-open submenu
        const tierOneBtn = e.target.closest('.tc-tier-one-navigation-item');
        if (tierOneBtn && !e.target.closest('.tc-mobile-submenu')) {
          tierOneBtn.classList.add('js-is-active');
          const submenu = tierOneBtn.nextElementSibling;
          if (submenu) submenu.classList.add('js-is-active');
          return;
        }

        // Back button: tier-two submenu → tier-one list
        if (e.target.closest('.tc-tier-two-go-back')) {
          const active = drawer.querySelector('.tc-tier-one-navigation-item.js-is-active');
          if (active) {
            active.classList.remove('js-is-active');
            const submenu = active.nextElementSibling;
            if (submenu) submenu.classList.remove('js-is-active');
          }
          return;
        }

        // Tier-two item — only if NOT inside a tier-three submenu
        const tierTwoBtn = e.target.closest('.tc-tier-two-navigation-item');
        if (tierTwoBtn && !e.target.closest('.tc-dropdown--tier-three')) {
          tierTwoBtn.classList.add('js-is-active');
          const submenu = tierTwoBtn.nextElementSibling;
          if (submenu) submenu.classList.add('js-is-active');
          return;
        }

        // Back button: tier-three submenu → tier-two list
        if (e.target.closest('.tc-tier-three-go-back')) {
          const active = drawer.querySelector('.tc-tier-two-navigation-item.js-is-active');
          if (active) {
            active.classList.remove('js-is-active');
            const submenu = active.nextElementSibling;
            if (submenu) submenu.classList.remove('js-is-active');
          }
          return;
        }
      });
    }

     _openMobileDrawer() {
      const drawer = this.querySelector('[data-mobile-drawer]');
      if (!drawer) return;
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this._syncFooterHeight(); // ← new — measure right as drawer becomes visible
    }

    _closeMobileDrawer() {
      const drawer = this.querySelector('[data-mobile-drawer]');
      if (!drawer) return;
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      drawer.querySelectorAll('.js-is-active').forEach(el => {
        el.classList.remove('js-is-active');
      });
    }

    // ── Scroll state ────────────────────────────────────────────────────────

    _bindScrollState() {
      const update = () => {
        if (window.scrollY > 40) {
          document.body.classList.add('js-scrolling');
        } else {
          document.body.classList.remove('js-scrolling');
        }
      };
      update();
      document.addEventListener('scroll', update, { passive: true });
    }

    // ── Click outside ───────────────────────────────────────────────────────

    _bindClickOutside() {
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this._closeAll();
        }
      });
    }

    // ── Escape key ──────────────────────────────────────────────────────────

    _bindEscape() {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this._closeAll();
      });
    }

    // ── Close everything ────────────────────────────────────────────────────

    _closeAll() {
      this._closeAllDropdowns();
      this._closeMobileDrawer();
      document.body.classList.remove('js-search-visible');
    }
  }

    if (!customElements.get('tc-site-header')) {
    customElements.define('tc-site-header', TcSiteHeader);
  }

})();